import { Injectable } from '@angular/core';
import { ConfigProvider } from "../../config-provider.service";
import { UtilsProvider } from "../../common/providers/utils-provider.service";
import { ApiBaseService } from "../../common/providers/api-base-service";
import { LangsProvider } from "../../common/providers/langs-provider";
import { CurrentUser } from "../../common/providers/current-user";
import { Organization } from "../../common/models/organization";
import { Observable } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { OrganizationMembership_OrganizationRole } from "../../common/models/organization-membership_organization-role";
import { OrganizationMembership } from "../../common/models/organization-membership";
import { OrganizationRole } from "../../common/models/organization-role";
import { User } from "../../common/models/user";

@Injectable({
  providedIn: 'root'
})
export class OrganizationsProvider {
  // caching by code
  private organizationCache: Map<string, Observable<Organization>> = new Map();
  constructor(
    private api: ApiBaseService,
    private currentUser: CurrentUser,
    private utilsProvider: UtilsProvider,
    private langsProvider: LangsProvider,
  ) {}

  public getOrganizations(): Observable<Organization[]> {
    return this.api.call('get', 'organizations/', {}, this.currentUser.getAccessToken()).pipe(
      map((data: any) => {
        this.organizationCache.clear();
        return Organization.fromAPIList(data);
      })
    );
  }

  public getOrganizationRoles(): Observable<OrganizationRole[]> {
    return this.api.call('get', 'organizations/roles/', {}, this.currentUser.getAccessToken()).pipe(
      map((data: any) => {
        return OrganizationRole.fromAPIList(data, []);
      })
    );
  }

  public getOrganizationRoleByOrganizationMembership(userId: number): Observable<OrganizationMembership_OrganizationRole[]> {
    const params = {
      "filters": [
        {"field": "userId", "operator": "=", "value": userId},
      ],
    };
    return this.api.call('get', 'organization-membership-organization-roles', params, this.currentUser.getAccessToken()).pipe(
      map((data: any) => {
        // Assuming the API response structure has a key that contains the roles, adjust as necessary.
        return OrganizationMembership_OrganizationRole.fromAPIList(data);
      })
    );
  }

  public getOrganizationUsersRows(organization: Organization): Observable<UserOrganizationRow[]> {
    return this.api.call('get', 'organizations/memberships', {organizationId: organization.id}, this.currentUser.getAccessToken()).pipe(
      map((data: any) => {
        const rows: UserOrganizationRow[] = [];
        for (let i = 0; i < data.length; i++) {
          const roles: any[] = [];
          const rolesIds: number[] = [];
          for (let j = 0; j < data[i].organizationMembershipOrganizationRoles.length; j++) {
            roles.push({
              role: OrganizationRole.fromAPI(data[i].organizationMembershipOrganizationRoles[j].organizationRole,data[i].translations ),
              membershipRole: OrganizationMembership_OrganizationRole.fromAPI(data[i].organizationMembershipOrganizationRoles[j])
            });
            rolesIds.push(data[i].organizationMembershipOrganizationRoles[j].organizationRoleId);
          }

          rows.push({
            organization,
            user: User.fromAPI(data[i].user),
            membership: OrganizationMembership.fromAPI(data[i]),
            rolesIds,
            roles
          });
        }

        rows.sort((a: UserOrganizationRow, b: UserOrganizationRow) => {
          const aName: string = a.user.firstName?.toLowerCase() + ' ' + a.user.lastName?.toLowerCase();
          const bName: string = b.user.firstName?.toLocaleLowerCase() + ' ' + b.user.lastName?.toLowerCase();
          if (aName < bName) { return -1; }
          else if (aName > bName) { return 1; }
          return 0;
        });

        return rows;
      })
    );
  }

  public saveUserRoles(membershipId: number, rolesIds: number[]): Observable<OrganizationMembership_OrganizationRole[]> {
    return this.api.call('post', 'organizations/memberships/' + membershipId + '/roles',
      { organizationRolesIds: rolesIds },
      this.currentUser.getAccessToken()
    ).pipe(
      map((data: any) => {
        this.organizationCache.clear();
        return OrganizationMembership_OrganizationRole.fromAPIList(data.organizationMembershipOrganizationRoles);
      })
    );
  }

  public saveMembership(membership: OrganizationMembership): Observable<OrganizationMembership> {
    return this.api.call(            'post',
      'organizations/memberships/' + (membership.id ? membership.id : 0),
      this.utilsProvider.buildModelParams([membership], this.langsProvider.getLangIds())[0],
      this.currentUser.getAccessToken()
    ).pipe(
      map((result: any) => {
        this.organizationCache.clear();
        return OrganizationMembership.fromAPI(result.data);
      })
    );
  }

  public removeMembership(membership: OrganizationMembership): Observable<void> {
    this.organizationCache.clear();
    return this.api.call(            'delete',
      'organizations/memberships/' + membership.id,
      {},
      this.currentUser.getAccessToken()
    );
  }

  public getByCode(code: string): Observable<Organization> {
    if (!this.organizationCache.has(code)) {
      const params = { code };
      const obs = this.api.call('get', 'organizations/', params, this.currentUser.getAccessToken()).pipe(
        map((result: any) => Organization.fromAPI(result.data[0])),
        shareReplay(1)
      );
      this.organizationCache.set(code, obs);
    }
    return this.organizationCache.get(code)!;
  }
}

export interface UserOrganizationRow {
  organization: Organization;
  user: User;
  membership: OrganizationMembership;
  rolesIds: number[];
  roles: OrganizationRole[];
}
